import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useStaff } from '@/features/staff/useStaff';
import { useCreateLead, useUpdateLead, type CreateLeadInput } from './useLeads';
import { LEAD_STAGE_LABELS, LEAD_STAGE_ORDER, type Lead, type LeadStage } from './types';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  edit?: Lead;
}

const UNASSIGNED = '__none__';

const parseBrl = (input: string): number | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const parsed = parseFloat(trimmed.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

export function LeadModal({ open, onOpenChange, edit }: Props) {
  const { data: staff = [] } = useStaff();
  const create = useCreateLead();
  const update = useUpdateLead();

  const [name, setName] = useState('');
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [stage, setStage] = useState<LeadStage>('new');
  const [estimatedValue, setEstimatedValue] = useState('');
  const [source, setSource] = useState('');
  const [notes, setNotes] = useState('');
  const [assigneeUserId, setAssigneeUserId] = useState<string>(UNASSIGNED);

  const assignableStaff = staff.filter((s) => s.active && s.user_id);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    if (edit) {
      setName(edit.name);
      setContactName(edit.contact_name ?? '');
      setPhone(edit.phone ?? '');
      setEmail(edit.email ?? '');
      setStage(edit.stage);
      setEstimatedValue(
        edit.estimated_value != null
          ? edit.estimated_value.toFixed(2).replace('.', ',')
          : '',
      );
      setSource(edit.source ?? '');
      setNotes(edit.notes ?? '');
      setAssigneeUserId(edit.assigned_to_user_id ?? UNASSIGNED);
    } else {
      setName('');
      setContactName('');
      setPhone('');
      setEmail('');
      setStage('new');
      setEstimatedValue('');
      setSource('');
      setNotes('');
      setAssigneeUserId(UNASSIGNED);
    }
  }, [open, edit]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;

    const assignee = assigneeUserId === UNASSIGNED ? null : assigneeUserId;

    if (edit) {
      await update.mutateAsync({
        id: edit.id,
        updates: {
          name: trimmed,
          contact_name: contactName.trim() || null,
          phone: phone.trim() || null,
          email: email.trim() || null,
          stage,
          estimated_value: parseBrl(estimatedValue),
          source: source.trim() || null,
          notes: notes.trim() || null,
          assigned_to_user_id: assignee,
        },
      });
    } else {
      const input: CreateLeadInput = {
        name: trimmed,
        contact_name: contactName.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
        stage,
        estimated_value: parseBrl(estimatedValue),
        source: source.trim() || null,
        notes: notes.trim() || null,
        assigned_to_user_id: assignee,
      };
      await create.mutateAsync(input);
    }
    onOpenChange(false);
  };

  const submitting = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-brand">{edit ? 'Editar lead' : 'Novo lead'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="mt-2 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="lead-name">Clínica / Dentista *</Label>
            <Input
              id="lead-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Clínica Sorriso Pleno"
              required
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="lead-contact">Contato</Label>
              <Input
                id="lead-contact"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="Nome da pessoa"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lead-phone">Telefone</Label>
              <Input
                id="lead-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(00) 00000-0000"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lead-email">E-mail</Label>
            <Input
              id="lead-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contato@clinica.com"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Estágio</Label>
              <Select value={stage} onValueChange={(v) => setStage(v as LeadStage)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_STAGE_ORDER.map((s) => (
                    <SelectItem key={s} value={s}>
                      {LEAD_STAGE_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lead-value">Valor estimado (R$)</Label>
              <Input
                id="lead-value"
                value={estimatedValue}
                onChange={(e) => setEstimatedValue(e.target.value)}
                placeholder="0,00"
                inputMode="decimal"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="lead-source">Origem</Label>
              <Input
                id="lead-source"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="Indicação, Instagram…"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Responsável</Label>
              <Select value={assigneeUserId} onValueChange={setAssigneeUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED}>Sem responsável</SelectItem>
                  {assignableStaff.map((s) => (
                    <SelectItem key={s.id} value={s.user_id as string}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lead-notes">Observações</Label>
            <Textarea
              id="lead-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Detalhes da conversa, próximos passos…"
              rows={3}
            />
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting}>
              {edit ? 'Salvar' : 'Criar lead'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
